import { useCallback, useEffect, useRef, useState } from 'react';

import {
  EntityRefUtil,
  SignatureArrayFormElementAnswerSchema,
  SignatureArrayFormElement as SignatureArrayFormElementType,
  SignatureEntry,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import ReactSignatureCanvas from 'react-signature-canvas';
import { z } from 'zod';

import { CloseSmallIcon } from '@/components/icons/close-small';
import { EraseIcon } from '@/components/icons/erase';
import { SignatureIcon } from '@/components/icons/signature';
import { UndoIcon } from '@/components/icons/undo';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { getSitemateUserQueryOptions } from '@/queries/assets';
import { AWAGlobalApiService } from '@/services/global-api/awa-global-api.service';
import { userService } from '@/services/global-api/user.service';
import { useIsAWA } from '@/utils/awa';
import { convertImageUrl } from '@/utils/image-url';

import { ConfigLabel } from '../config-label';
import { FormFieldError } from '../form-field-error';

export const SignatureArrayFormElement = ({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  isPreview = false,
  error,
}: {
  element: SignatureArrayFormElementType;
  value?: z.infer<typeof SignatureArrayFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof SignatureArrayFormElementAnswerSchema>['value']) => void;
  onBlur?: () => void;
  readonly: boolean;
  isPreview: boolean;
  error?: string;
}) => {
  const isManualSignature = element.config?.isManualSignature ?? true;
  const toast = useToast();
  const dialogCanvasRef = useRef<ReactSignatureCanvas | null>(null);
  const { isAWA } = useIsAWA();
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);

  // State for manual signature dialog inputs
  const [manualFirstName, setManualFirstName] = useState('');
  const [manualLastName, setManualLastName] = useState('');
  const [manualCompany, setManualCompany] = useState('');

  // Store canvas data to prevent clearing when keyboard appears
  const [canvasData, setCanvasData] = useState<string | null>(null);
  const canvasDataRef = useRef<string | null>(null);

  // Central function to save canvas data
  const saveCanvasData = useCallback(() => {
    if (dialogCanvasRef.current && !dialogCanvasRef.current.isEmpty()) {
      const data = dialogCanvasRef.current.toDataURL();
      canvasDataRef.current = data;
      setCanvasData(data);
    }
  }, []);

  // Fetch current user's Sitemate User data to get signatureUrl
  const { data: sitemateUser } = useQuery({ ...getSitemateUserQueryOptions({ isAWA }), enabled: !isManualSignature && !isPreview });

  const { mutateAsync: uploadData } = useMutation({
    mutationFn: (params: Parameters<typeof AWAGlobalApiService.uploadData>[0]) => AWAGlobalApiService.uploadData(params),
  });

  const { mutateAsync: uploadSignature } = useMutation({
    mutationFn: (params: { data: string; fileName: string }) => userService.uploadSignature(params.data, params.fileName),
  });

  // Convert value to array format (handle legacy string format)
  const signatures: SignatureEntry[] = (() => {
    if (!value) return [];

    // Legacy format: single string (old manual signature)
    if (typeof value === 'string') {
      return [
        {
          firstName: '',
          lastName: '',
          companyName: undefined,
          signatureUrl: value,
          timestamp: new Date().toISOString(),
        },
      ];
    }

    // New format: array of signatures
    if (Array.isArray(value)) {
      return value;
    }

    return [];
  })();

  // Restore canvas data if it was cleared unexpectedly (e.g., by keyboard)
  useEffect(() => {
    if (showSignatureDialog && canvasDataRef.current && dialogCanvasRef.current) {
      const canvas = dialogCanvasRef.current;
      // Check if canvas is empty but we have saved data
      if (canvas.isEmpty()) {
        canvas.fromDataURL(canvasDataRef.current);
      }
    }
  }, [showSignatureDialog, canvasData]);

  const handleClearDialogSignature = () => {
    dialogCanvasRef.current?.clear();
    setCanvasData(null);
    canvasDataRef.current = null;
  };

  const handleContinueWithDialogSignature = async () => {
    const dataUrl = dialogCanvasRef.current?.toDataURL();
    if (!dataUrl || dialogCanvasRef.current?.isEmpty()) {
      toast.error('Please provide a signature before continuing.');
      return;
    }

    if (isManualSignature) {
      // Manual signature - validate inputs
      if (!manualFirstName.trim() || !manualLastName.trim()) {
        toast.error('Please enter first name and last name.');
        return;
      }

      const newSignature: SignatureEntry = {
        firstName: manualFirstName.trim(),
        lastName: manualLastName.trim(),
        companyName: manualCompany.trim() || undefined,
        signatureUrl: dataUrl,
        timestamp: new Date().toISOString(),
      };

      const updatedSignatures = [...signatures, newSignature];
      onChange(updatedSignatures);
      onBlur?.();
      setShowSignatureDialog(false);
      dialogCanvasRef.current?.clear();
      setManualFirstName('');
      setManualLastName('');
      setManualCompany('');
    } else {
      // User signature (existing behavior)
      if (!sitemateUser) {
        toast.error('User profile not found. Please try again.');
        return;
      }

      try {
        // Upload the signature to persist on the user's profile
        const fileName = `${sitemateUser.id}-${sitemateUser.firstName}-${sitemateUser.lastName}-${sitemateUser.companyName}-signature.png`;
        if (isAWA) {
          await uploadData({
            data: dataUrl,
            type: 'signature',
            fileName,
          });
        } else {
          await uploadSignature({ data: dataUrl, fileName });
        }

        const newSignature: SignatureEntry = {
          userId: sitemateUser.id,
          firstName: sitemateUser.firstName || '',
          lastName: sitemateUser.lastName || '',
          companyName: sitemateUser.companyName || undefined,
          signatureUrl: dataUrl,
          timestamp: new Date().toISOString(),
        };

        const updatedSignatures = [...signatures, newSignature];
        onChange(updatedSignatures);
        onBlur?.();
        setShowSignatureDialog(false);
        dialogCanvasRef.current?.clear();

        toast.success('Signature saved successfully.');
      } catch (error) {
        console.error('Error uploading signature:', error);
        toast.error('Failed to save signature. Please try again.');
      }
    }
  };

  const handleAddSignature = () => {
    if (isPreview) {
      toast.warning('You cannot use signatures in a form preview');
      return;
    } else if (readonly) {
      return;
    }

    if (isManualSignature) {
      // Manual signature - always show dialog
      setShowSignatureDialog(true);
    } else {
      // User signature (existing behavior)
      if (!sitemateUser) {
        toast.error('User profile not found. Please try again.');
        return;
      }

      if (!sitemateUser?.signatureUrl) {
        setShowSignatureDialog(true);
        return;
      }

      const newSignature: SignatureEntry = {
        userId: sitemateUser.id,
        firstName: sitemateUser.firstName || '',
        lastName: sitemateUser.lastName || '',
        companyName: sitemateUser.companyName || undefined,
        signatureUrl: sitemateUser.signatureUrl,
        timestamp: new Date().toISOString(),
      };

      const updatedSignatures = [...signatures, newSignature];
      onChange(updatedSignatures);
      onBlur?.();
    }
  };

  const handleRemoveSignature = (index: number) => {
    if (isPreview) {
      toast.warning('You cannot use signatures in a form preview');
      return;
    } else if (readonly) {
      return;
    }

    const updatedSignatures = signatures.filter((_, i) => i !== index);
    onChange(updatedSignatures.length > 0 ? updatedSignatures : null);
    onBlur?.();
  };

  return (
    <div className="space-y-2">
      <div>
        <Label required={element.required}>{element.label}</Label>

        <div className="space-y-0">
          {/* Display existing signatures */}
          {signatures.map((signature, index) => (
            <div key={index}>
              <div className="space-y-1">
                <div className="relative">
                  <div
                    className={cn(
                      'align-center border-grey-3 relative flex h-[110px] cursor-not-allowed justify-center rounded border',
                      !isAWA ? 'max-w-[300px]' : 'w-full',
                    )}>
                    <img
                      src={convertImageUrl(signature.signatureUrl)}
                      alt={`Signature by ${EntityRefUtil.createUserFullNameRef(signature)}`}
                      className="pointer-events-none h-auto object-contain"
                    />
                    {!readonly && !isPreview && (
                      <button
                        type="button"
                        aria-label="Remove signature"
                        onClick={() => handleRemoveSignature(index)}
                        className="bg-grey-4 hover:bg-grey-5 absolute top-0 right-0 flex h-6 w-6 items-center justify-center rounded-bl text-white">
                        <CloseSmallIcon size={30} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="text-secondary-text text-sm font-normal">{EntityRefUtil.createUserFullNameRef(signature)}</div>
                  {isManualSignature && signature.companyName && <div className="text-secondary-text text-sm font-normal">{signature.companyName}</div>}
                  <div className="text-secondary-text text-sm font-normal">{dayjs(new Date(signature.timestamp)).format('MMM DD, YYYY HH:mm A')}</div>
                </div>
              </div>
              {index < signatures.length - 1 && <div className={cn('border-grey-2 mt-1 mb-2 border-t', !isAWA ? 'max-w-[300px]' : 'w-full')} />}
            </div>
          ))}

          {(!readonly || isPreview) && (
            <Button
              type="button"
              variant="success"
              disabled={isPreview}
              onClick={handleAddSignature}
              className={cn('gap-1', isPreview && 'opacity-70', isAWA && 'w-full')}>
              {isManualSignature ? 'Sign Manually' : 'Sign'} <SignatureIcon />
            </Button>
          )}
        </div>
      </div>

      <FormFieldError error={error} />

      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="w-[366px] rounded-lg px-2 py-4" onInteractOutside={(e) => e.preventDefault()}>
          {!isManualSignature && (
            <DialogHeader>
              <DialogTitle>
                <div className="text-default-text text-left text-xl font-medium">Create your signature</div>
              </DialogTitle>
              <DialogDescription>
                <div className="text-default-text text-left text-sm font-normal">Use this to sign off and approve forms in Gearbelt.</div>
              </DialogDescription>
            </DialogHeader>
          )}
          <div className="space-y-2">
            {isManualSignature && (
              <>
                <Input value={manualFirstName} onChange={(e) => setManualFirstName(e.target.value)} onFocus={saveCanvasData} placeholder="First name..." />
                <Input value={manualLastName} onChange={(e) => setManualLastName(e.target.value)} onFocus={saveCanvasData} placeholder="Last name..." />
                <Input value={manualCompany} onChange={(e) => setManualCompany(e.target.value)} onFocus={saveCanvasData} placeholder="Company..." />
                <div>
                  <Label>Sign Here</Label>
                </div>
              </>
            )}
            <div className="border-grey-3 relative w-full rounded-md border bg-white">
              <ReactSignatureCanvas
                ref={dialogCanvasRef}
                penColor="black"
                canvasProps={{
                  width: 334,
                  height: 150,
                  style: { width: '100%', height: '150px', touchAction: 'none' },
                  onTouchStart: () => {
                    // Blur input fields when starting to draw
                    if ('activeElement' in document && document.activeElement instanceof HTMLElement) {
                      document.activeElement.blur();
                    }
                    // Save current canvas state before drawing
                    saveCanvasData();
                  },
                  onMouseDown: saveCanvasData,
                }}
              />
              {isManualSignature && (
                <button
                  type="button"
                  onClick={handleClearDialogSignature}
                  className="hover:bg-grey-1 absolute top-1 right-1 flex items-center justify-center rounded-full bg-white p-1 shadow-sm"
                  aria-label="Clear signature">
                  <UndoIcon size={20} />
                </button>
              )}
            </div>

            {!isManualSignature && (
              <div className="flex w-full flex-col gap-2">
                <Button type="button" variant="grey" onClick={handleClearDialogSignature} className="w-full text-sm font-normal">
                  <EraseIcon size={16} className="mr-2" />
                  Clear signature
                </Button>
                <Button type="button" variant="default" onClick={handleContinueWithDialogSignature} className="w-full text-sm font-normal">
                  {isManualSignature ? 'Save' : 'Continue'}
                </Button>
              </div>
            )}
            {isManualSignature && (
              <div className="flex w-full flex-row gap-2">
                <Button type="button" variant="default" onClick={() => setShowSignatureDialog(false)} className="w-1/2 text-sm font-normal">
                  Cancel
                </Button>
                <Button type="button" variant="success" onClick={handleContinueWithDialogSignature} className="w-1/2 text-sm font-normal">
                  Save
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export function SignatureArrayFormElementMock({ element }: { element: SignatureArrayFormElementType }) {
  const isManualSignature = element.config?.isManualSignature ?? true;

  // Both manual and user signature modes now display the same button-based UI
  return (
    <div className="flex flex-wrap gap-4">
      <Button type="button" variant="success" className="gap-1 opacity-70" disabled>
        {isManualSignature ? 'Sign Manually' : 'Sign'} <SignatureIcon />
      </Button>
    </div>
  );
}

export function SignatureArrayFormElementConfig({
  element,
  onChange,
}: {
  element: SignatureArrayFormElementType;
  onChange?: (updates: Partial<SignatureArrayFormElementType>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>
    </div>
  );
}
